import {
  ArrowDownUp,
  BookOpen,
  Check,
  CheckCircle,
  Circle,
  Download,
  FileType2,
  Grid2x2,
  Grid3x3,
  KeyRound,
  Play,
  RectangleHorizontal,
  RectangleVertical,
  Star,
  Trash2,
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { addEpisodeFavorite, deleteEpisode, removeEpisodeFavorite, updateEpisodeProgress } from '@/api/api'
import { deleteConvertedEpisode } from '@/api/services/ebook'
import type { EpisodeList } from '@/api/type'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { AuthImage } from '@/components/ui/auth-image'
import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { useProductDownload } from '@/hooks/useEpisodeDownload'
import { useSubmitLock } from '@/hooks/useSubmitLock'
import { parseUtcDate } from '@/lib/date-utils'
import { buildDownloadItems } from '@/lib/download-utils'
import { formatSecondsJa, isDurationMime } from '@/lib/time-format'
import { cn, formatBytes } from '@/lib/utils'
import { useAppStore } from '@/store/useAppStore'
import { useProgressStore } from '@/store/useProgressStore'
import { useUISettingsStore } from '@/store/useUISettingsStore'
import type { EbookInputFormat } from '@/types/ebook-conversion'
import {
  getInputFormatFromMime,
  HIDDEN_MIME_TYPES,
  isConvertibleFormat,
  NON_READABLE_MIME_TYPES,
} from '@/types/ebook-conversion'

import { EbookConversionDialog } from './ebook-conversion-dialog'
import { EpisodeDownloadButton } from './episode-download-button'
import { GrantAccessDialog } from './grant-access-dialog'

type SortOrder = 'desc' | 'asc'

/** エピソードを複数選択して一括操作するモード（null は通常表示） */
type SelectionMode = 'grant' | 'delete' | null

// 1週間以内にアップロードされたかどうかを判定
const isWithinOneWeek = (dateString: string | null | undefined): boolean => {
  if (!dateString) return false
  const uploadTimestamp = parseUtcDate(dateString).getTime()
  if (Number.isNaN(uploadTimestamp)) return false
  const oneWeekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000
  return uploadTimestamp >= oneWeekAgo
}

// MIMEタイプから表示用ラベルを取得
const getMimeLabel = (mimeType: string): string => {
  const mimeLabels: Record<string, string> = {
    'application/epub+zip': 'EPUB',
    'application/pdf': 'PDF',
    'application/vnd.comicbook+zip': 'CBZ',
    'application/vnd.comicbook-rar': 'CBR',
    'application/x-mobipocket-ebook': 'MOBI',
    'application/vnd.amazon.ebook': 'AZW3',
  }
  return mimeLabels[mimeType] || mimeType.split('/').pop()?.toUpperCase() || mimeType
}

// index 昇順。index 同値（x / tver / hulu 等）は保存順（episode_id）で並べる
const compareEpisodeAsc = (a: EpisodeList, b: EpisodeList): number => a.index - b.index || a.episode_id - b.episode_id

// エピソードの表示名を取得
const episodeLabel = (episode: { index: number; custom_name: string | null }, displayIndex?: number): string => {
  const number = displayIndex ?? episode.index
  return episode.custom_name ? `${number}. ${episode.custom_name}` : `Episode ${number}`
}

// 変換されたEpisodeかどうかを判定（descriptionにJSON形式の__converted__マーカーがある）
const isConvertedEpisode = (description: string | null | undefined): boolean => {
  if (!description) return false
  try {
    const parsed = JSON.parse(description)
    return parsed.__converted__ === true
  } catch {
    return false
  }
}

type EpisodeDrawerProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  episodes: EpisodeList[]
  lastEpisodeId: number | null
  productTitle: string
  onEpisodeSelect: (episodeId: number) => void
  onEpisodeUpdate?: (episodeId: number, isCompleted: boolean) => void
  /** お気に入り状態を変更したときに呼ばれる */
  onFavoriteUpdate?: (episodeId: number, isFavorite: boolean) => void
  domain?: string
  /** プロダクトID（オフラインダウンロード用） */
  productId?: number
  /** 変換完了後にデータを再取得するコールバック */
  onRefresh?: () => void
}

export const EpisodeDrawer = ({
  open,
  onOpenChange,
  episodes,
  lastEpisodeId,
  productTitle,
  onEpisodeSelect,
  onEpisodeUpdate,
  onFavoriteUpdate,
  domain,
  productId,
  onRefresh,
}: EpisodeDrawerProps) => {
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc')
  // ローカルで読了状態を管理（楽観的更新用）
  const [localCompletedMap, setLocalCompletedMap] = useState<Map<number, boolean>>(new Map())
  // ローカルでお気に入り状態を管理（楽観的更新用）
  const [localFavoriteMap, setLocalFavoriteMap] = useState<Map<number, boolean>>(new Map())
  // グリッドカラム・アスペクト比設定
  const { episodeGridColumns, setEpisodeGridColumns, episodeAspectRatio, setEpisodeAspectRatio } = useUISettingsStore()

  // 管理者権限チェック
  const hasFullAccess = useAppStore((state) => state.hasFullAccess)

  // 電子書籍変換ダイアログの状態
  const [conversionDialogOpen, setConversionDialogOpen] = useState(false)
  const [conversionTarget, setConversionTarget] = useState<{
    episodeId: number
    episodeTitle: string
    inputFormat: EbookInputFormat
  } | null>(null)

  // 削除確認ダイアログの状態
  const [deleteTarget, setDeleteTarget] = useState<EpisodeList | null>(null)
  const { isSubmitting: isDeleting, run } = useSubmitLock()

  // エピソードの複数選択モード（アクセス権付与 / 一括削除で共用）
  const [selectionMode, setSelectionMode] = useState<SelectionMode>(null)
  const [selectedEpisodeIds, setSelectedEpisodeIds] = useState<Set<number>>(new Set())
  const [grantDialogOpen, setGrantDialogOpen] = useState(false)
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false)
  const { isSubmitting: isBulkDeleting, run: runBulkDelete } = useSubmitLock()
  const isSelectionMode = selectionMode !== null
  const selectionRingClass = selectionMode === 'delete' ? 'ring-2 ring-red-400' : 'ring-2 ring-blue-400'
  const selectionCheckClass = selectionMode === 'delete' ? 'bg-red-500 border-red-500' : 'bg-blue-500 border-blue-500'

  const toggleEpisodeSelection = useCallback((episodeId: number) => {
    setSelectedEpisodeIds((prev) => {
      const next = new Set(prev)
      if (next.has(episodeId)) {
        next.delete(episodeId)
      } else {
        next.add(episodeId)
      }
      return next
    })
  }, [])

  const exitSelectionMode = useCallback(() => {
    setSelectionMode(null)
    setSelectedEpisodeIds(new Set())
  }, [])

  // 選択モードのトグル（別モードが有効なときは選択をリセットして切り替える）
  const toggleSelectionMode = useCallback((mode: Exclude<SelectionMode, null>) => {
    setSelectedEpisodeIds(new Set())
    setSelectionMode((prev) => (prev === mode ? null : mode))
  }, [])

  // Drawer が閉じたら選択モードもリセット（Drawer 外クリックや別 Drawer への切替時）
  useEffect(() => {
    if (!open) {
      setSelectionMode(null)
      setSelectedEpisodeIds(new Set())
      setGrantDialogOpen(false)
      setBulkDeleteDialogOpen(false)
    }
  }, [open])

  // 対象プロダクトが切り替わったら選択モードをリセット
  useEffect(() => {
    setSelectionMode(null)
    setSelectedEpisodeIds(new Set())
  }, [productId])

  // JSON/CUE など表示対象外のmime_typeを除外したエピソード一覧
  const visibleEpisodes = useMemo(
    () => episodes.filter((ep) => !ep.mime_type || !HIDDEN_MIME_TYPES.has(ep.mime_type)),
    [episodes],
  )

  // Product内のユニークなMIMEタイプを取得
  // octet-stream は mime_type 未設定のエピソードをオフライン保存したときの代替値で、実際の形式ではないため数えない
  const availableFormats = useMemo(() => {
    const mimeTypes = visibleEpisodes
      .map((ep) => ep.mime_type)
      .filter((mt): mt is string => !!mt && mt !== 'application/octet-stream')
    return [...new Set(mimeTypes)]
  }, [visibleEpisodes])

  // 複数フォーマットがある、またはブラウザで直接読めないフォーマット（azw/azw3等）のみの場合はフォーマット選択UIを表示
  // mime_typeがすべてnullの場合は通常のエピソード一覧を表示
  const showFormatSelector =
    availableFormats.length > 1 ||
    (availableFormats.length > 0 && availableFormats.every((fmt) => NON_READABLE_MIME_TYPES.has(fmt)))

  // 表示用の話数。index が全話同じ値のドメイン（x / tver / hulu 等）は並び順で 1..n を振り直す
  const displayIndexMap = useMemo(() => {
    const map = new Map<number, number>()
    const hasDistinctIndex = new Set(visibleEpisodes.map((ep) => ep.index)).size > 1
    const ordered = [...visibleEpisodes].sort(compareEpisodeAsc)
    ordered.forEach((ep, i) => map.set(ep.episode_id, hasDistinctIndex ? ep.index : i + 1))
    return map
  }, [visibleEpisodes])

  const labelOf = useCallback(
    (episode: EpisodeList) => episodeLabel(episode, displayIndexMap.get(episode.episode_id)),
    [displayIndexMap],
  )

  // ソートされたエピソード一覧
  const sortedEpisodes = useMemo(() => {
    return [...visibleEpisodes].sort((a, b) =>
      sortOrder === 'desc' ? compareEpisodeAsc(b, a) : compareEpisodeAsc(a, b),
    )
  }, [visibleEpisodes, sortOrder])

  // エピソードの読了状態を取得（ローカル優先）
  const getIsCompleted = useCallback(
    (episode: EpisodeList) => {
      if (localCompletedMap.has(episode.episode_id)) {
        return localCompletedMap.get(episode.episode_id)!
      }
      return episode.is_completed
    },
    [localCompletedMap],
  )

  // エピソードのお気に入り状態を取得（ローカル優先）
  const getIsFavorite = useCallback(
    (episode: EpisodeList) => {
      const local = localFavoriteMap.get(episode.episode_id)
      if (local !== undefined) return local
      return episode.is_favorite
    },
    [localFavoriteMap],
  )

  const episodesByIndex = useMemo(() => [...visibleEpisodes].sort(compareEpisodeAsc), [visibleEpisodes])

  // 続きを読むエピソードを決定
  // 最後に読んだエピソードが読了済みなら、そこから先で最初の未読了エピソードへ進む
  const continueEpisode = useMemo(() => {
    const firstUnread = episodesByIndex.find((ep) => !getIsCompleted(ep))

    const currentPos = lastEpisodeId ? episodesByIndex.findIndex((ep) => ep.episode_id === lastEpisodeId) : -1
    if (currentPos === -1) {
      return firstUnread ?? episodesByIndex[0]
    }

    const lastReadEpisode = episodesByIndex[currentPos]
    if (!getIsCompleted(lastReadEpisode)) {
      return lastReadEpisode
    }

    // 以降がすべて読了済みなら、飛ばした前方の未読了へ戻る
    const nextUnread = episodesByIndex.slice(currentPos + 1).find((ep) => !getIsCompleted(ep))
    return nextUnread ?? firstUnread ?? lastReadEpisode
  }, [episodesByIndex, lastEpisodeId, getIsCompleted])

  const hasReadHistory = lastEpisodeId !== null
  const isShowingNextEpisode = continueEpisode && lastEpisodeId && continueEpisode.episode_id !== lastEpisodeId

  const handleContinueClick = () => {
    if (continueEpisode) {
      onEpisodeSelect(continueEpisode.episode_id)
    }
  }

  const handleSortToggle = () => {
    setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'))
  }

  // 読了状態をトグル
  const handleToggleCompleted = useCallback(
    async (e: React.MouseEvent, episode: EpisodeList) => {
      e.stopPropagation() // 親のクリックイベントを防止

      const currentCompleted = getIsCompleted(episode)
      const newCompleted = !currentCompleted

      // 楽観的更新
      setLocalCompletedMap((prev) => {
        const newMap = new Map(prev)
        newMap.set(episode.episode_id, newCompleted)
        return newMap
      })

      try {
        const nextProgress = episode.progress ?? (newCompleted ? (episode.num_pages ?? 1) : 0)
        // 一覧のローカルデータにも反映させる
        useProgressStore.getState().applyEpisodeProgress(episode.episode_id, nextProgress, newCompleted)
        await updateEpisodeProgress(episode.episode_id, {
          progress: nextProgress,
          is_completed: newCompleted,
        })
        onEpisodeUpdate?.(episode.episode_id, newCompleted)
      } catch (error) {
        console.error('Error toggling completed:', error)
        // エラー時はロールバック
        useProgressStore.getState().applyEpisodeProgress(episode.episode_id, episode.progress ?? 0, currentCompleted)
        setLocalCompletedMap((prev) => {
          const newMap = new Map(prev)
          newMap.delete(episode.episode_id)
          return newMap
        })
        toast.error('更新に失敗しました')
      }
    },
    [getIsCompleted, onEpisodeUpdate],
  )

  // お気に入り状態をトグル
  const handleToggleFavorite = useCallback(
    async (e: React.MouseEvent, episode: EpisodeList) => {
      e.stopPropagation() // 親のクリックイベントを防止

      const newFavorite = !getIsFavorite(episode)

      // 楽観的更新
      setLocalFavoriteMap((prev) => {
        const newMap = new Map(prev)
        newMap.set(episode.episode_id, newFavorite)
        return newMap
      })

      try {
        if (newFavorite) {
          await addEpisodeFavorite(episode.episode_id)
        } else {
          await removeEpisodeFavorite(episode.episode_id)
        }
        onFavoriteUpdate?.(episode.episode_id, newFavorite)
      } catch (error) {
        console.error('Error toggling favorite:', error)
        // エラー時はロールバック
        setLocalFavoriteMap((prev) => {
          const newMap = new Map(prev)
          newMap.delete(episode.episode_id)
          return newMap
        })
        toast.error('お気に入りの更新に失敗しました')
      }
    },
    [getIsFavorite, onFavoriteUpdate],
  )

  // フォーマット選択時にそのエピソードを直接開く
  const handleFormatSelect = useCallback(
    (episodeId: number) => {
      onEpisodeSelect(episodeId)
    },
    [onEpisodeSelect],
  )

  // 変換ダイアログを開く
  const handleOpenConversion = useCallback(
    (episode: EpisodeList) => {
      if (!episode.mime_type) return

      const inputFormat = getInputFormatFromMime(episode.mime_type)
      if (!inputFormat) return

      // まずDrawerを閉じる
      onOpenChange(false)

      setConversionTarget({
        episodeId: episode.episode_id,
        episodeTitle: labelOf(episode),
        inputFormat,
      })
      setConversionDialogOpen(true)
    },
    [onOpenChange, labelOf],
  )

  // 変換されたEpisodeを削除
  const handleDeleteEpisode = useCallback(async () => {
    if (!deleteTarget) return

    await run(async () => {
      try {
        await deleteConvertedEpisode(deleteTarget.episode_id)
        setDeleteTarget(null)
        // データを再取得してリストを更新
        onRefresh?.()
      } catch (error) {
        console.error('Delete error:', error)
        toast.error('削除に失敗しました')
      }
    })
  }, [deleteTarget, onRefresh, run])

  // 選択したエピソードをまとめて削除（ファイルごと物理削除）
  const handleDeleteSelected = useCallback(async () => {
    const targetIds = Array.from(selectedEpisodeIds)
    if (targetIds.length === 0) return

    await runBulkDelete(async () => {
      let deletedCount = 0
      let deletedFileCount = 0
      const failedIds: number[] = []

      for (const episodeId of targetIds) {
        try {
          const result = await deleteEpisode(episodeId)
          deletedCount += 1
          deletedFileCount += result.deleted_files.length
        } catch (error) {
          console.error('Delete episode error:', error)
          failedIds.push(episodeId)
        }
      }

      setBulkDeleteDialogOpen(false)
      exitSelectionMode()

      if (deletedCount > 0) {
        toast.success(`${deletedCount}件（${deletedFileCount}ファイル）を削除しました`)
      }
      if (failedIds.length > 0) {
        toast.error(`${failedIds.length}件の削除に失敗しました`)
      }

      onRefresh?.()
    })
  }, [selectedEpisodeIds, exitSelectionMode, onRefresh, runBulkDelete])

  return (
    <>
      <BottomSheet title={productTitle} open={open} onOpenChange={onOpenChange}>
        {/* 複数フォーマットがある場合はフォーマット選択のみ表示 */}
        {showFormatSelector ? (
          <div className="px-4 pb-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-zinc-400">フォーマットを選択してください</p>
              <div className="flex items-center gap-1">
                {hasFullAccess && productId && (
                  <SelectionModeButtons selectionMode={selectionMode} onToggle={toggleSelectionMode} />
                )}
                {productId && domain && (
                  <DownloadAllButton episodes={visibleEpisodes} productId={productId} domain={domain} />
                )}
              </div>
            </div>
            <div className="flex flex-col gap-2">
              {visibleEpisodes.map((episode) => {
                const format = episode.mime_type
                if (!format) return null
                const isCompleted = getIsCompleted(episode)
                const isFavorite = getIsFavorite(episode)
                const progress = episode.progress ?? 0
                const numPages = episode.num_pages ?? 0
                // num_pagesがある場合は割合で計算、なければprogressをそのまま使用
                const progressPercent = numPages > 0 ? Math.round((progress / numPages) * 100) : progress
                const converted = isConvertedEpisode(episode.description)
                const nonReadable = NON_READABLE_MIME_TYPES.has(format)
                // 変換エピソードの場合はラベルに区別用テキストを付与、読めないフォーマットは「XXXを変換」
                const label = converted
                  ? `${getMimeLabel(format)} (${episode.custom_name ?? `変換 #${episode.episode_id}`})`
                  : nonReadable
                    ? `${getMimeLabel(format)}を変換`
                    : getMimeLabel(format)

                return (
                  <div key={episode.episode_id} className="relative flex items-center gap-2">
                    <Button
                      variant="outline"
                      className={cn(
                        'relative flex-1 justify-start gap-3 text-white border-zinc-700 overflow-hidden',
                        isCompleted ? 'bg-green-900/30 hover:bg-green-900/40' : 'bg-zinc-800 hover:bg-zinc-700',
                        isSelectionMode && selectedEpisodeIds.has(episode.episode_id) && selectionRingClass,
                      )}
                      onClick={() => {
                        if (isSelectionMode) {
                          toggleEpisodeSelection(episode.episode_id)
                          return
                        }
                        if (nonReadable && !converted) {
                          handleOpenConversion(episode)
                        } else {
                          handleFormatSelect(episode.episode_id)
                        }
                      }}
                    >
                      {/* 進捗バー（背景） */}
                      {progressPercent > 0 && !isCompleted && (
                        <div
                          className="absolute inset-y-0 left-0 bg-blue-600/20"
                          style={{ width: `${progressPercent}%` }}
                        />
                      )}
                      <div className="relative flex items-center gap-3 w-full">
                        <BookOpen size={18} />
                        <span>{label}</span>
                        {episode.file_size != null && (
                          <span className="text-xs text-zinc-500">{formatBytes(episode.file_size)}</span>
                        )}
                        {progressPercent > 0 && !isCompleted && (
                          <span className="ml-auto text-xs text-zinc-400">
                            {numPages > 0 ? `${progress}/${numPages}` : `${progressPercent}%`}
                          </span>
                        )}
                      </div>
                    </Button>
                    {/* お気に入りトグルボタン */}
                    {!isSelectionMode && (
                      <button
                        type="button"
                        className={cn(
                          'p-2! rounded-md transition-colors hover:bg-zinc-700',
                          isFavorite ? 'text-amber-400' : 'text-white/40 hover:text-white',
                        )}
                        onClick={(e) => handleToggleFavorite(e, episode)}
                        title={isFavorite ? 'お気に入りから削除' : 'お気に入りに追加'}
                      >
                        <Star size={20} fill={isFavorite ? 'currentColor' : 'none'} />
                      </button>
                    )}
                    {/* 読了トグルボタン（読めないフォーマットの未変換エピソードには不要） */}
                    {!isSelectionMode && !(nonReadable && !converted) && (
                      <button
                        type="button"
                        className={cn(
                          'p-2! rounded-md transition-colors',
                          'hover:bg-zinc-700',
                          isCompleted ? 'text-green-500' : 'text-white/70 hover:text-white',
                        )}
                        onClick={(e) => handleToggleCompleted(e, episode)}
                      >
                        {isCompleted ? <CheckCircle size={20} strokeWidth={4} /> : <Circle size={20} strokeWidth={4} />}
                      </button>
                    )}
                    {/* オフラインダウンロードボタン */}
                    {!isSelectionMode && productId && domain && (
                      <EpisodeDownloadButton episode={episode} productId={productId} domain={domain} />
                    )}
                    {/* 変換ボタン（読めないフォーマットはメインボタンが変換を兼ねるので不要） */}
                    {!isSelectionMode && hasFullAccess && !converted && !nonReadable && isConvertibleFormat(format) && (
                      <button
                        type="button"
                        className="p-2! rounded-md transition-colors hover:bg-zinc-700 text-zinc-500 hover:text-blue-400"
                        onClick={(e) => {
                          e.stopPropagation()
                          handleOpenConversion(episode)
                        }}
                        title="フォーマット変換"
                      >
                        <FileType2 size={20} />
                      </button>
                    )}
                    {/* 削除ボタン（管理者のみ、変換されたEpisodeのみ） */}
                    {!isSelectionMode && hasFullAccess && converted && (
                      <button
                        type="button"
                        className="p-2! rounded-md transition-colors hover:bg-zinc-700 text-zinc-500 hover:text-red-400"
                        onClick={(e) => {
                          e.stopPropagation()
                          setDeleteTarget(episode)
                        }}
                        title="削除"
                      >
                        <Trash2 size={20} />
                      </button>
                    )}
                    {/* 選択モード時の選択オーバーレイ */}
                    {isSelectionMode && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-20 pointer-events-none">
                        <div
                          className={cn(
                            'w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors',
                            selectedEpisodeIds.has(episode.episode_id)
                              ? selectionCheckClass
                              : 'bg-white/20 border-white/60',
                          )}
                        >
                          {selectedEpisodeIds.has(episode.episode_id) && <Check size={18} className="text-white" />}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ) : (
          // BottomSheet 側の space-y-6 が効かないよう 1 要素にまとめる
          <div>
            {/* ヘッダーアクション: 続きから読む / 最初から読む + ソートトグル */}
            {/* flex-wrap-reverse: 1行に収まらない場合はボタン群が上の行に折り返される */}
            <div className="flex flex-wrap-reverse items-center gap-2 px-4 pt-3 pb-1.5 sticky top-0 bg-background z-20">
              <Button
                variant="default"
                size="sm"
                className="flex max-w-full shrink-0 items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-md"
                onClick={handleContinueClick}
              >
                {hasReadHistory ? (
                  <>
                    <Play size={16} className="shrink-0" />
                    <span className="truncate">
                      {isShowingNextEpisode
                        ? `${continueEpisode ? labelOf(continueEpisode) : ''} を読む`
                        : `${continueEpisode ? labelOf(continueEpisode) : ''} から続きを読む`}
                    </span>
                  </>
                ) : (
                  <>
                    <BookOpen size={16} className="shrink-0" />
                    最初から読む
                  </>
                )}
              </Button>

              <div className="flex items-center gap-1 ml-auto shrink-0">
                {/* 選択モード（アクセス権付与 / 一括削除）トグルボタン */}
                {hasFullAccess && productId && (
                  <SelectionModeButtons selectionMode={selectionMode} onToggle={toggleSelectionMode} />
                )}

                {/* 全話ダウンロードボタン */}
                {productId && domain && <DownloadAllButton episodes={episodes} productId={productId} domain={domain} />}

                {/* アスペクト比切替トグル */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-zinc-400 hover:text-white hover:bg-zinc-800"
                  onClick={() => setEpisodeAspectRatio(episodeAspectRatio === 'landscape' ? 'portrait' : 'landscape')}
                >
                  {episodeAspectRatio === 'landscape' ? (
                    <RectangleHorizontal size={16} />
                  ) : (
                    <RectangleVertical size={16} />
                  )}
                </Button>

                {/* グリッドカラム切替トグル */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-zinc-400 hover:text-white hover:bg-zinc-800"
                  onClick={() => setEpisodeGridColumns(episodeGridColumns === 2 ? 3 : 2)}
                >
                  {episodeGridColumns === 2 ? <Grid2x2 size={16} /> : <Grid3x3 size={16} />}
                </Button>

                <Button
                  variant="ghost"
                  size="sm"
                  className="flex items-center gap-1 text-zinc-400 hover:text-white hover:bg-zinc-800"
                  onClick={handleSortToggle}
                >
                  <ArrowDownUp size={16} />
                  {sortOrder === 'desc' ? '新しい順' : '古い順'}
                </Button>
              </div>
            </div>

            {/* エピソードグリッド */}
            <div className="px-4 pt-1 pb-6 overflow-y-auto">
              <div className={cn('grid gap-2', episodeGridColumns === 2 ? 'grid-cols-2' : 'grid-cols-3')}>
                {sortedEpisodes.map((episode) => {
                  const isLastRead = episode.episode_id === lastEpisodeId
                  const progress =
                    episode.num_pages === 1
                      ? (episode.progress ?? 0)
                      : episode.progress && episode.num_pages
                        ? Math.round((episode.progress / episode.num_pages) * 100)
                        : 0
                  const showThumbnail = episode.thumbnail_url !== null
                  const isCompleted = getIsCompleted(episode)
                  const isFavorite = getIsFavorite(episode)
                  const isNew = isWithinOneWeek(episode.upload_date)

                  // タイトル下のメタ情報（再生時間・ファイルサイズ）
                  const metaLabel = [
                    isDurationMime(episode.mime_type) && episode.num_pages > 0
                      ? formatSecondsJa(episode.num_pages)
                      : null,
                    episode.file_size != null ? formatBytes(episode.file_size) : null,
                  ]
                    .filter(Boolean)
                    .join(' ・ ')

                  // サムネイル（またはカード）に重ねるバッジ類
                  const overlayBadges = (
                    <>
                      {/* 続きから / NEW バッジ（両立しない） */}
                      {(isLastRead || isNew) && (
                        <div className="absolute top-1 left-1.5 z-10 flex">
                          {isLastRead ? (
                            <span className="bg-zinc-700/80 text-blue-400 text-[9px] font-medium leading-none tracking-wide px-1.5 py-0.5 rounded backdrop-blur-sm border border-blue-500/30">
                              続きから
                            </span>
                          ) : (
                            <span className="bg-zinc-700/80 text-emerald-400 text-[9px] font-medium leading-none tracking-wide px-1.5 py-0.5 rounded backdrop-blur-sm border border-emerald-500/30">
                              NEW
                            </span>
                          )}
                        </div>
                      )}

                      {/* 読了トグルボタン */}
                      {!isSelectionMode && (
                        <div
                          className={cn(
                            'absolute top-1 right-1 p-1 rounded-full transition-colors z-10',
                            'bg-black/50 hover:bg-black/60 drop-shadow-md',
                            isCompleted ? 'text-green-400' : 'text-white/80 hover:text-white',
                          )}
                          onClick={(e) => handleToggleCompleted(e, episode)}
                        >
                          {isCompleted ? (
                            <CheckCircle size={18} strokeWidth={4} />
                          ) : (
                            <Circle size={18} strokeWidth={4} />
                          )}
                        </div>
                      )}

                      {/* オフラインダウンロードバッジ */}
                      {!isSelectionMode && productId && domain && (
                        <EpisodeDownloadButton episode={episode} productId={productId} domain={domain} compact />
                      )}

                      {/* お気に入りトグルボタン */}
                      {!isSelectionMode && (
                        <div
                          className={cn(
                            'absolute bottom-1 right-1 p-1 rounded-full transition-colors z-10',
                            'bg-black/50 hover:bg-black/60 drop-shadow-md',
                            isFavorite ? 'text-amber-400' : 'text-white/70 hover:text-white',
                          )}
                          onClick={(e) => handleToggleFavorite(e, episode)}
                        >
                          <Star size={16} fill={isFavorite ? 'currentColor' : 'none'} />
                        </div>
                      )}

                      {/* 進捗バー */}
                      {progress > 0 && (
                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-zinc-600 overflow-hidden">
                          <div
                            className={cn('h-full transition-all', isCompleted ? 'bg-green-500' : 'bg-blue-500')}
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      )}
                    </>
                  )

                  return (
                    <button
                      key={episode.episode_id}
                      className={cn(
                        'relative flex flex-col items-center justify-center',
                        'rounded-lg transition-all overflow-hidden p-0!',
                        'bg-zinc-800 hover:bg-zinc-700',
                        isLastRead && 'ring-2 ring-blue-500 bg-zinc-700',
                        isSelectionMode && selectedEpisodeIds.has(episode.episode_id) && selectionRingClass,
                        showThumbnail ? 'p-0' : 'p-3',
                      )}
                      onClick={() => {
                        if (isSelectionMode) {
                          toggleEpisodeSelection(episode.episode_id)
                          return
                        }
                        onEpisodeSelect(episode.episode_id)
                      }}
                    >
                      {showThumbnail ? (
                        <>
                          {/* サムネイル（エピソード固有のサムネイルがある場合のみ） */}
                          <div
                            className={cn(
                              'relative w-full overflow-hidden',
                              episodeAspectRatio === 'landscape' ? 'aspect-video' : 'aspect-3/4',
                            )}
                          >
                            <AuthImage
                              src={`/api${episode.thumbnail_url}`}
                              alt={labelOf(episode)}
                              className="w-full h-full object-cover"
                            />
                            {overlayBadges}
                          </div>

                          {/* エピソード名・メタ情報（サムネイルの下） */}
                          <div className="w-full px-1.5 py-1">
                            <span className="block text-[10px] font-bold text-white truncate leading-tight text-left">
                              {labelOf(episode)}
                            </span>
                            {metaLabel && (
                              <span className="block text-[9px] text-zinc-400 truncate leading-tight text-left">
                                {metaLabel}
                              </span>
                            )}
                          </div>
                        </>
                      ) : (
                        <>
                          {/* エピソード番号（サムネイルなしの場合） */}
                          <div className="text-white font-medium text-lg">{labelOf(episode)}</div>

                          {/* メタ情報（再生時間・ファイルサイズ） */}
                          {metaLabel && <span className="text-[10px] text-zinc-500">{metaLabel}</span>}

                          {overlayBadges}
                        </>
                      )}

                      {/* 選択モード時の選択オーバーレイ */}
                      {isSelectionMode && (
                        <div className="absolute inset-0 bg-black/40 flex items-center justify-center z-20 pointer-events-none">
                          <div
                            className={cn(
                              'w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors',
                              selectedEpisodeIds.has(episode.episode_id)
                                ? selectionCheckClass
                                : 'bg-white/20 border-white/60',
                            )}
                          >
                            {selectedEpisodeIds.has(episode.episode_id) && <Check size={18} className="text-white" />}
                          </div>
                        </div>
                      )}
                    </button>
                  )
                })}
              </div>
            </div>
          </div>
        )}

        {/* 選択モード時のアクションバー（シート内下部に固定） */}
        {isSelectionMode && (
          <div
            className={cn(
              'sticky bottom-0 left-0 right-0 backdrop-blur-sm py-3 px-4 z-30 flex items-center justify-between',
              selectionMode === 'delete' ? 'bg-red-600/95' : 'bg-blue-600/95',
            )}
          >
            <span className="text-white font-medium">{selectedEpisodeIds.size}件選択中</span>
            <div className="flex gap-2">
              <button
                onClick={exitSelectionMode}
                className="px-4 py-1.5 text-sm bg-white/20 hover:bg-white/30 text-white rounded-md transition-colors"
              >
                キャンセル
              </button>
              <button
                onClick={() => (selectionMode === 'delete' ? setBulkDeleteDialogOpen(true) : setGrantDialogOpen(true))}
                disabled={selectedEpisodeIds.size === 0}
                className={cn(
                  'px-4 py-1.5 text-sm bg-white hover:bg-white/90 font-bold rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed',
                  selectionMode === 'delete' ? 'text-red-600' : 'text-blue-600',
                )}
              >
                {selectionMode === 'delete' ? '削除' : '付与'}
              </button>
            </div>
          </div>
        )}
      </BottomSheet>

      {/* 電子書籍変換ダイアログ */}
      {conversionTarget && (
        <EbookConversionDialog
          open={conversionDialogOpen}
          onOpenChange={setConversionDialogOpen}
          episodeId={conversionTarget.episodeId}
          episodeTitle={conversionTarget.episodeTitle}
          inputFormat={conversionTarget.inputFormat}
          productTitle={productTitle}
          onComplete={() => {
            onRefresh?.()
            onOpenChange(true)
          }}
        />
      )}

      {/* 削除確認ダイアログ */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>変換ファイルを削除しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget?.mime_type && getMimeLabel(deleteTarget.mime_type)}形式のファイルを削除します。
              この操作は取り消せません。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>キャンセル</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteEpisode}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? '削除中...' : '削除'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* 選択したエピソードの一括削除確認ダイアログ */}
      <AlertDialog
        open={bulkDeleteDialogOpen}
        onOpenChange={(open) => {
          if (!isBulkDeleting) setBulkDeleteDialogOpen(open)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{selectedEpisodeIds.size}件のエピソードを削除しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              「{productTitle}」から選択したエピソードを、実ファイルごと削除します。この操作は取り消せません。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBulkDeleting}>キャンセル</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                // 削除完了までダイアログを開いたままにするため、既定の閉じる動作を止める
                e.preventDefault()
                handleDeleteSelected()
              }}
              disabled={isBulkDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isBulkDeleting ? '削除中...' : '削除'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* アクセス権限付与ダイアログ */}
      {productId && (
        <GrantAccessDialog
          open={grantDialogOpen}
          onOpenChange={(open) => {
            setGrantDialogOpen(open)
            if (!open) {
              exitSelectionMode()
            }
          }}
          mode="episode"
          targetIds={Array.from(selectedEpisodeIds)}
          targetLabel={productTitle}
        />
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// 選択モード切替ボタン（アクセス権付与 / 一括削除）
// ---------------------------------------------------------------------------

const SelectionModeButtons = ({
  selectionMode,
  onToggle,
}: {
  selectionMode: SelectionMode
  onToggle: (mode: Exclude<SelectionMode, null>) => void
}) => (
  <>
    <Button
      variant="ghost"
      size="icon"
      className={cn(
        'h-8 w-8 hover:bg-zinc-800',
        selectionMode === 'grant' ? 'text-blue-400 hover:text-blue-300' : 'text-zinc-400 hover:text-white',
      )}
      onClick={() => onToggle('grant')}
      title={selectionMode === 'grant' ? 'アクセス権付与モード終了' : 'アクセス権付与モード'}
    >
      <KeyRound size={16} />
    </Button>
    <Button
      variant="ghost"
      size="icon"
      className={cn(
        'h-8 w-8 hover:bg-zinc-800',
        selectionMode === 'delete' ? 'text-red-400 hover:text-red-300' : 'text-zinc-400 hover:text-white',
      )}
      onClick={() => onToggle('delete')}
      title={selectionMode === 'delete' ? '削除モード終了' : '削除モード'}
    >
      <Trash2 size={16} />
    </Button>
  </>
)

// ---------------------------------------------------------------------------
// 全話ダウンロードボタン
// ---------------------------------------------------------------------------

const DownloadAllButton = ({
  episodes,
  productId,
  domain,
}: {
  episodes: EpisodeList[]
  productId: number
  domain: string
}) => {
  const { downloadProduct } = useProductDownload()
  const [showConfirm, setShowConfirm] = useState(false)

  const totalSizeInfo = useMemo(() => {
    const known = episodes.filter((ep) => ep.file_size != null)
    const total = known.reduce((sum, ep) => sum + (ep.file_size ?? 0), 0)
    const hasUnknown = known.length < episodes.length
    return { total, hasUnknown, allUnknown: known.length === 0 }
  }, [episodes])

  const handleDownloadAll = useCallback(() => {
    setShowConfirm(false)
    downloadProduct(buildDownloadItems(episodes, productId, domain))
  }, [episodes, productId, domain, downloadProduct])

  const sizeLabel = totalSizeInfo.allUnknown
    ? '合計: 不明'
    : totalSizeInfo.hasUnknown
      ? `合計: 約 ${formatBytes(totalSizeInfo.total)}（一部不明）`
      : `合計: ${formatBytes(totalSizeInfo.total)}`

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-zinc-400 hover:text-white hover:bg-zinc-800"
        onClick={() => setShowConfirm(true)}
        title="全話をオフラインにダウンロード"
      >
        <Download size={16} />
      </Button>

      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>全話ダウンロード</AlertDialogTitle>
            <AlertDialogDescription>
              全 {episodes.length} エピソードをダウンロードしますか？
              <br />
              <span className="font-medium text-zinc-300">{sizeLabel}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>キャンセル</AlertDialogCancel>
            <AlertDialogAction onClick={handleDownloadAll}>ダウンロード</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
